import CachedIcon from "@mui/icons-material/Cached"
import Alert from "@mui/material/Alert"
import Button from "@mui/material/Button"
import Card from "@mui/material/Card"
import CardContent from "@mui/material/CardContent"
import Link from "@mui/material/Link"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"

import type { LiveChatWidgetCdnConfig } from "@models/live_chat_widget"

function Field({ label, value }: { label: string; value?: string | null }) {
  return (
    <Stack gap={0.25} sx={{ minWidth: "12rem" }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
        {value || "—"}
      </Typography>
    </Stack>
  )
}

export default function CdnTargetCard({
  config,
  translate,
  busy,
  canWrite,
  onInvalidate
}: {
  config?: LiveChatWidgetCdnConfig | null
  translate: (key: string) => string
  busy: boolean
  canWrite: boolean
  onInvalidate: () => void
}) {
  const configured = !!config?.bucket && !!config?.distributionId

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack gap={2}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={1}
          >
            <Typography variant="subtitle1">
              {translate("live_chat_widget_target_title")}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<CachedIcon />}
              disabled={busy || !canWrite || !configured}
              onClick={onInvalidate}
            >
              {translate("live_chat_widget_invalidate_cache")}
            </Button>
          </Stack>

          {!configured && (
            <Alert severity="error">
              {translate("live_chat_widget_not_configured")}
            </Alert>
          )}

          <Stack direction="row" gap={3} flexWrap="wrap">
            <Field
              label={translate("live_chat_widget_bucket")}
              value={config?.bucket}
            />
            <Field
              label={translate("live_chat_widget_region")}
              value={config?.region}
            />
            <Field
              label={translate("live_chat_widget_prefix")}
              value={config?.prefix}
            />
            <Field
              label={translate("live_chat_widget_distribution")}
              value={config?.distributionId}
            />
            <Stack gap={0.25} sx={{ minWidth: "12rem" }}>
              <Typography variant="caption" color="text.secondary">
                {translate("live_chat_widget_cdn_url")}
              </Typography>
              {config?.cdnUrl ? (
                <Link
                  href={config.cdnUrl}
                  target="_blank"
                  rel="noreferrer"
                  variant="body2"
                  sx={{ fontFamily: "monospace" }}
                >
                  {config.cdnUrl}
                </Link>
              ) : (
                <Typography variant="body2">—</Typography>
              )}
            </Stack>
          </Stack>

          <Typography variant="caption" color="text.secondary">
            {translate("live_chat_widget_target_env_hint")}
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  )
}
